import React, { useEffect, useState } from "react";
import "./order-summary-card.css";

const formatPrice = (amount) => {
  if (amount === null || amount === undefined || amount === "") {
    return "N/A";
  }

  let numericAmount = amount;
  if (typeof amount === "string") {
    const cleaned = amount.replace(/[₦,\s]/g, "");
    numericAmount = parseFloat(cleaned);
  }

  if (typeof numericAmount === "number" && !isNaN(numericAmount)) {
    return `₦${numericAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  return amount;
};

const formatSampleType = (value) => {
  if (!value || typeof value !== "string") {
    return "N/A";
  }

  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const OrderSummaryCard = ({ orderId, className = "" }) => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(Boolean(orderId));
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchOrderDetails = async () => {
      if (!orderId) {
        setOrderDetails(null);
        setLoading(false);
        setError(null);
        return;
      }

      if (!window.wpApiSettings?.nonce) {
        setError("Authentication token not found. Please log in again.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/wp-json/dbsb/v1/orders/${orderId}`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error("Failed to fetch order details.");
        }

        const data = await response.json();
        if (isMounted) {
          setOrderDetails(data);
        }
      } catch (err) {
        if (err.name === "AbortError") return;

        if (isMounted) {
          setError(err.message || "Failed to fetch order details.");
          setOrderDetails(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOrderDetails();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [orderId]);

  const headingText =
    orderDetails?.order_id || orderId ? `Order ID: ${orderDetails?.order_id || orderId}` : "Order details";

  const details = orderDetails?.test || {};

  const detailItems = [
    { label: "Test name", value: details.name || "N/A" },
    {
      label: "Price",
      value: details.price ? formatPrice(details.price) : "N/A",
      className: "price-detail",
    },
    {
      label: "Estimated processing time",
      value: details.estimated_processing_time || "N/A",
    },
    {
      label: "Sample type required",
      value: formatSampleType(details.sample_type),
      className: "sample-type-item",
    },
  ];

  const renderContent = () => {
    if (!orderId) {
      return (
        <p className="order-summary-card__status">
          Order details unavailable.
        </p>
      );
    }

    if (loading) {
      return (
        <p className="order-summary-card__status">
          Loading order information...
        </p>
      );
    }

    if (error) {
      return (
        <p className="order-summary-card__status order-summary-card__status--error">
          {error}
        </p>
      );
    }

    if (!orderDetails) {
      return (
        <p className="order-summary-card__status">
          Order details unavailable.
        </p>
      );
    }

    return detailItems.map((item, index) => (
      <div
        key={`${item.label}-${index}`}
        className={`detail-item ${item.className || ""}`}
      >
        <span>{item.label}</span>
        <span>{item.value}</span>
      </div>
    ));
  };

  return (
    <div className={`order-summary-card ${className}`.trim()}>
      <h3>{headingText}</h3>
      <div className="order-summary-card__content">{renderContent()}</div>
    </div>
  );
};

export default OrderSummaryCard;


